/**
 * Utility functions to transform data between app format (camelCase) and database format (snake_case)
 */

// Transform session from database to app format
export const transformSessionFromDB = (dbSession) => {
  if (!dbSession) return null;
  return {
    id: dbSession.id,
    name: dbSession.name,
    description: dbSession.description || '',
    gameType: dbSession.game_type || 'Texas Hold\'em',
    stakes: dbSession.stakes || '',
    location: dbSession.location || '',
    isActive: dbSession.is_active !== undefined ? dbSession.is_active : true,
    createdAt: dbSession.created_at,
    lastUpdated: dbSession.last_updated || dbSession.created_at,
  };
};

// Transform session from app to database format
export const transformSessionToDB = (appSession) => {
  return {
    name: appSession.name,
    description: appSession.description || '',
    game_type: appSession.gameType || 'Texas Hold\'em',
    stakes: appSession.stakes || '',
    location: appSession.location || '',
    is_active: appSession.isActive !== undefined ? appSession.isActive : true,
  };
};

// Transform player from database to app format
export const transformPlayerFromDB = (dbPlayer) => {
  if (!dbPlayer) return null;
  
  const cashOutHistory = Array.isArray(dbPlayer.cash_out_history) ? dbPlayer.cash_out_history : [];
  const buyInHistory = Array.isArray(dbPlayer.buy_in_history) ? dbPlayer.buy_in_history : [];
  
  // Calculate totalCashOut from history if it exists, otherwise use stored value
  const calculatedTotalCashOut = cashOutHistory.reduce((sum, cashOut) => {
    return sum + (parseFloat(cashOut.amount) || 0);
  }, 0);
  
  // Use calculated value if history exists, otherwise use stored value
  const totalCashOut = cashOutHistory.length > 0 
    ? calculatedTotalCashOut 
    : parseFloat(dbPlayer.total_cash_out || 0);
  
  return {
    id: dbPlayer.id,
    sessionId: dbPlayer.session_id,
    name: dbPlayer.name,
    profileColor: dbPlayer.profile_color,
    totalBuyIn: parseFloat(dbPlayer.total_buy_in || 0),
    totalCashOut: totalCashOut,
    buyInHistory: buyInHistory,
    cashOutHistory: cashOutHistory,
    createdAt: dbPlayer.created_at,
  };
};

// Transform player from app to database format
// Only includes fields that are actually provided (for partial updates)
export const transformPlayerToDB = (appPlayer) => {
  const dbData = {};
  
  // Only include fields that are explicitly provided
  if (appPlayer.name !== undefined) {
    dbData.name = appPlayer.name;
  }
  if (appPlayer.profileColor !== undefined) {
    dbData.profile_color = appPlayer.profileColor;
  }
  if (appPlayer.totalBuyIn !== undefined) {
    dbData.total_buy_in = appPlayer.totalBuyIn;
  }
  if (appPlayer.totalCashOut !== undefined) {
    dbData.total_cash_out = appPlayer.totalCashOut;
  }
  if (appPlayer.buyInHistory !== undefined) {
    dbData.buy_in_history = appPlayer.buyInHistory;
  }
  if (appPlayer.cashOutHistory !== undefined) {
    dbData.cash_out_history = appPlayer.cashOutHistory;
  }
  
  return dbData;
};

// Transform expense from database to app format
export const transformExpenseFromDB = (dbExpense) => {
  if (!dbExpense) return null;
  return {
    id: dbExpense.id,
    sessionId: dbExpense.session_id,
    amount: parseFloat(dbExpense.amount || 0),
    description: dbExpense.description,
    category: dbExpense.category || 'General',
    playerId: dbExpense.player_id || null,
    playerName: dbExpense.player_name || null,
    timestamp: dbExpense.timestamp,
  };
};

// Transform expense from app to database format
export const transformExpenseToDB = (appExpense) => {
  return {
    amount: appExpense.amount,
    description: appExpense.description,
    category: appExpense.category || 'General',
    player_id: appExpense.playerId || null,
    player_name: appExpense.playerName || null,
  };
};

// Transform master player from database to app format
export const transformMasterPlayerFromDB = (dbPlayer) => {
  if (!dbPlayer) return null;
  return {
    id: dbPlayer.id,
    name: dbPlayer.name,
    profileColor: dbPlayer.profile_color,
    email: dbPlayer.email || '',
    phone: dbPlayer.phone || '',
    notes: dbPlayer.notes || '',
    createdAt: dbPlayer.created_at,
    updatedAt: dbPlayer.updated_at || dbPlayer.created_at,
  };
};

// Transform master player from app to database format
export const transformMasterPlayerToDB = (appPlayer) => {
  return {
    name: appPlayer.name,
    profile_color: appPlayer.profileColor,
    email: appPlayer.email || '',
    phone: appPlayer.phone || '',
    notes: appPlayer.notes || '',
  };
};

